import { useState } from "react"
import {ChevronDown, HelpCircle} from 'lucide-react'

interface FAQItem {
  question: string
  answer: string
}

const defaultFAQs: FAQItem[] = [
  {
    question: "How do I get a loan to start a restaurant?",
    answer: "Starting a restaurant typically requires securing an SBA 7(a) loan, a traditional bank loan, or alternative financing. You'll need a solid business plan, financial projections, good credit (typically 680+), and some collateral. Most lenders want to see restaurant industry experience and a personal investment of 10-30% of the total project cost. Use our calculator above to estimate your monthly payments for different loan amounts and terms."
  },
  {
    question: "What are the best restaurant equipment financing companies?",
    answer: "Top restaurant equipment financing companies include Crest Capital, Balboa Capital, National Funding, and Currency Capital. Many also use SBA 504 loans specifically for equipment purchases. Equipment financing typically ranges from $5,000 to $500,000 with terms of 2-7 years and rates between 6-25% depending on your credit profile and the equipment's useful life."
  },
  {
    question: "How much does it cost to open a Subway franchise?",
    answer: "A Subway franchise costs between $116,000 and $263,000 in total investment, with a franchise fee of $15,000. You'll need a minimum of $100,000 in liquid assets. Financing options include SBA loans, traditional bank loans, and Subway's own financing partnerships. Our calculator's Subway preset ($200k at 7% for 10 years) gives you a realistic monthly payment estimate."
  },
  {
    question: "What credit score do I need for a restaurant business loan?",
    answer: "For SBA loans, most lenders require a minimum credit score of 680, though some may accept 650. Traditional bank loans typically require 700+. Alternative lenders and equipment financing companies may approve scores as low as 550-600, but at higher interest rates (15-25% APR). The higher your score, the better rates and terms you'll qualify for."
  },
  {
    question: "Can I get an SBA loan to open a bar?",
    answer: "Yes, the SBA does provide loans for bars and nightclubs through the 7(a) loan program. However, businesses that derive more than 33% of their revenue from alcohol sales may face additional scrutiny. You'll need a comprehensive business plan, proper licensing, and strong financials. Check our dedicated Bar Financing page for more details."
  },
  {
    question: "How long does it take to get a restaurant business loan?",
    answer: "SBA loans typically take 30-90 days from application to funding. Traditional bank loans take 2-6 weeks. Online lenders and alternative financing can fund in as little as 1-7 business days, though at higher rates. Equipment financing often takes 1-3 weeks. Having all your documents ready (tax returns, business plan, financial statements) can significantly speed up the process."
  },
  {
    question: "What is the average interest rate for a restaurant loan?",
    answer: "Restaurant loan interest rates vary widely by loan type: SBA 7(a) loans range from 6-8%, traditional bank loans from 5-10%, equipment financing from 6-15%, and alternative/online lenders from 10-30%. Your actual rate depends on credit score, time in business, revenue, collateral, and the loan amount. Use our calculator to see how different rates affect your monthly payment."
  },
  {
    question: "Do I need a down payment for a restaurant loan?",
    answer: "Most restaurant loans require a down payment or equity injection. SBA loans typically require 10-20% down. Traditional bank loans may require 20-30%. Equipment financing sometimes offers 100% financing with no down payment. Franchise loans usually require 20-30% of the total franchise cost as a personal investment. A larger down payment often results in better interest rates."
  },
]

export default function FAQ({ items }: { items?: FAQItem[] }) {
  const faqs = items || defaultFAQs
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="py-16 sm:py-20">
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
          <HelpCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">FAQ</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white">Frequently Asked Questions</h2>
        <p className="text-slate-400 mt-2 max-w-xl mx-auto text-sm">Everything you need to know about restaurant financing and business loans.</p>
      </div>

      <div className="max-w-3xl mx-auto space-y-3">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="bg-[#0F2240]/60 border border-white/5 rounded-xl overflow-hidden hover:border-white/10 transition-colors"
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className="w-full flex items-center justify-between px-5 py-4 text-left"
            >
              <span className="text-sm font-medium text-white pr-4">{faq.question}</span>
              <ChevronDown
                className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ${
                  openIndex === index ? "rotate-180 text-emerald-400" : ""
                }`}
              />
            </button>
            {openIndex === index ? (
              <div className="px-5 pb-4 animate-fade-in">
                <p className="text-sm text-slate-400 leading-relaxed">{faq.answer}</p>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  )
}
